// src/mazot-takip/src/pages/Raporlar.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

/* ========== Helpers (HESAPLAMA AYNI KALDI) ========== */
const ALPHA = 0.00083;
const fmt = (n, d = 2) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: d })
    .format(Number(n || 0));
const safeUpper = (s) =>
  s ? String(s).toUpperCase().replaceAll("İ", "I").replaceAll("ı", "I") : s;
const num = (x) => (x == null ? 0 : Number(x) || 0);

function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toDate === "function") return ts.toDate().getTime();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  return 0;
}
function inRange(ts, sDate, eDate) {
  const ms = tsToMs(ts);
  return ms >= sDate.getTime() && ms <= eDate.getTime();
}
function getDefaultRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  const pad = (x) => String(x).padStart(2, "0");
  return {
    startStr: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    endStr: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
  };
}

/* ================== BİLEŞEN ================== */
export default function Raporlar() {
  const [{ startStr, endStr }, setRange] = useState(getDefaultRange());

  const [deliveries, setDeliveries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [plates, setPlates] = useState([]);
  const [people, setPeople] = useState([]);

  // Ortam sıcaklıkları (Open-Meteo ya da fallback)
  const [ambientByTank, setAmbientByTank] = useState({});
  const [loadingTemp, setLoadingTemp] = useState(false);

  /* ---- Data load ---- */
  useEffect(() => {
    (async () => {
      const [delSnap, trSnap, adjSnap, tkSnap, plSnap, peSnap] = await Promise.all([
        getDocs(collection(db, "fuel_deliveries")),
        getDocs(collection(db, "fuel_transactions")),
        getDocs(collection(db, "fuel_tank_adjustments")),
        getDocs(collection(db, "fuel_tanks")),
        getDocs(collection(db, "plates")),
        getDocs(collection(db, "personeller")),
      ]);
      setDeliveries(delSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setTransactions(trSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setAdjustments(adjSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      setTanks(
        tkSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((t) => t.active !== false)
      );
      setPlates(
        plSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((p) => p.deleted !== true)
      );
      setPeople(
        peSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((p) => p.deleted !== true)
      );
    })();
  }, []);

  /* ---- Lookups (AYNI) ---- */
  const plateMap = useMemo(() => {
    const m = new Map();
    plates.forEach((p) => {
      const key = safeUpper(p.plaka || p.plate || p.id);
      if (!key) return;
      m.set(key, { tesis: p.tesis || "GENEL", aracCinsi: p.aracCinsi || p.type || "-" });
    });
    return m;
  }, [plates]);
  const peopleMap = useMemo(() => {
    const m = new Map();
    people.forEach((p) => m.set(p.id, p));
    return m;
  }, [people]);

  /* ---- Son görülen tank sıcaklığı (AYNI) ---- */
  const lastTempByTank = useMemo(() => {
    const latest = new Map();
    const upd = (tankId, temp, date) => {
      if (!tankId) return;
      const ms = tsToMs(date);
      if (!Number.isFinite(ms)) return;
      if (typeof temp !== "number") return;
      const cur = latest.get(tankId);
      if (!cur || ms > cur.ms) latest.set(tankId, { ms, temp: Number(temp) });
    };
    deliveries.forEach((d) => {
      const t =
        (typeof d.temperatureC === "number" ? d.temperatureC : undefined) ??
        (typeof d.tempTank === "number" ? d.tempTank : undefined);
      upd(d.tankId, t, d.date);
    });
    transactions.forEach((t) => {
      const tt =
        (typeof t.temperatureC === "number" ? t.temperatureC : undefined) ??
        (typeof t.tempTank === "number" ? t.tempTank : undefined);
      upd(t.tankId, tt, t.date);
    });
    return latest;
  }, [deliveries, transactions]);

  /* ---- Open-Meteo ambient (AYNI) ---- */
  useEffect(() => {
    if (!tanks.length) return;
    (async () => {
      setLoadingTemp(true);
      const entries = await Promise.all(
        tanks.map(async (t) => {
          if (typeof t.latestTemperatureC === "number") return [t.id, Number(t.latestTemperatureC)];
          if (typeof t.lat === "number" && typeof t.lng === "number") {
            try {
              const url = `https://api.open-meteo.com/v1/forecast?latitude=${t.lat}&longitude=${t.lng}&current=temperature_2m`;
              const res = await fetch(url); const js = await res.json();
              const temp = js?.current?.temperature_2m;
              if (typeof temp === "number") return [t.id, Number(temp)];
            } catch {}
          }
          const fb = lastTempByTank.get(t.id)?.temp;
          return [t.id, (typeof fb === "number" ? fb : 15)];
        })
      );
      const map = {}; entries.forEach(([id, temp]) => (map[id] = temp));
      setAmbientByTank(map);
      setLoadingTemp(false);
    })();
  }, [tanks, lastTempByTank]);

  /* ================== HESAPLAMALAR (AYNI) ================== */
  const stockByTesisAll = useMemo(() => {
    const at15Field = (r) =>
      r.litersAt15 ?? r.liters_15 ?? r.at15 ?? r.corrected ?? r.correctedLiters ??
      r.litersCorrected ?? r.corrLiters ?? r.volumeAt15;
    const rawField = (r) =>
      r.litersRaw ?? r.rawLiters ?? r.volumeRaw ?? r.liters ?? r.deltaLiters;
    //const to15 = (L, T) => num(L) * (1 - ALPHA * (Number(T) - 15));
    const to15 = (L, T) => num(L) * (1 - ALPHA * (15 - Number(T)));

    const tesisByTank = new Map();
    tanks.forEach((t) => tesisByTank.set(t.id, t.tesis || "GENEL"));
    const getTesis = (obj) =>
      obj?.tesis ?? (obj?.tankId ? tesisByTank.get(obj.tankId) : undefined) ?? "GENEL";

    const pickT = (rec) => {
      const t1 = (typeof rec.temperatureC === "number" ? rec.temperatureC : undefined);
      const t2 = (typeof rec.tempTank === "number" ? rec.tempTank : undefined);
      const t3 = (rec.tankId != null && typeof ambientByTank[rec.tankId] === "number")
        ? ambientByTank[rec.tankId] : undefined;
      const t4 = (rec.tankId != null && typeof lastTempByTank.get(rec.tankId)?.temp === "number")
        ? lastTempByTank.get(rec.tankId).temp : undefined;
      return t1 ?? t2 ?? t3 ?? t4 ?? 15;
    };

    const out = new Map();
    const adj15ByTesis = {};
    const tempSum = {}; const tempCnt = {};

    const addOut = (tesis, rawAdd, corrAdd) => {
      if (!out.has(tesis)) out.set(tesis, { raw: 0, corrected: 0 });
      const o = out.get(tesis); o.raw += num(rawAdd); o.corrected += num(corrAdd);
    };
    const addTemp = (tesis, T) => {
      if (!Number.isFinite(T)) return;
      tempSum[tesis] = (tempSum[tesis] || 0) + T;
      tempCnt[tesis] = (tempCnt[tesis] || 0) + 1;
    };

    deliveries.forEach((d) => {
      const tesis = getTesis(d);
      const raw = num(rawField(d));
      const corr = (at15Field(d) != null) ? num(at15Field(d)) : to15(raw, pickT(d));
      addOut(tesis, raw, corr); addTemp(tesis, pickT(d));
    });

    transactions.forEach((t) => {
      if (t.deleted) return;
      if (!(t.status === "approved" && t.sourceType === "tank")) return;
      const tesis = getTesis(t);
      const raw = num(rawField(t));
      const corr = (at15Field(t) != null) ? num(at15Field(t)) : to15(raw, pickT(t));
      addOut(tesis, -raw, -corr); addTemp(tesis, pickT(t));
    });

    adjustments.forEach((a) => {
      const tesis = getTesis(a);
      const raw = num(rawField(a));
      const corr = (at15Field(a) != null) ? num(at15Field(a)) : to15(raw, pickT(a));
      addOut(tesis, raw, corr); adj15ByTesis[tesis] = (adj15ByTesis[tesis] || 0) + corr; addTemp(tesis, pickT(a));
    });

    const tempByTesis = {};
    Object.keys(tempSum).forEach((k) => { tempByTesis[k] = tempCnt[k] > 0 ? tempSum[k] / tempCnt[k] : 15; });
    return { out, adjByTesis: adj15ByTesis, tempByTesis };
  }, [deliveries, transactions, adjustments, tanks, ambientByTank, lastTempByTank]);

  /* ================== PLAKA SATIRLARI (DÜZELTİLDİ) ================== */
  const plateRows = useMemo(() => {
    const sDate = new Date(`${startStr}T00:00:00`);
    const eDate = new Date(`${endStr}T23:59:59`);

    const allTx = transactions
      .filter((t) => t.status === "approved" && t.sourceType === "tank" && t.deleted !== true)
      .map((t) => ({
        ...t,
        // plKey: normalize için (gruplama anahtarı)
        plKey: safeUpper(t.vehiclePlate) || "PLAKA-YOK",
        // plDisplay: veritabanındaki hali (ekranda gözükecek)
        plDisplay: t.vehiclePlate || "PLAKA-YOK",
        ms: tsToMs(t.date),
      }));

    // Gruplama: normalize edilmiş anahtar ile
    const byPlate = new Map();
    allTx.forEach((t) => {
      if (!byPlate.has(t.plKey)) byPlate.set(t.plKey, []);
      byPlate.get(t.plKey).push(t);
    });
    byPlate.forEach((arr) => arr.sort((a, b) => a.ms - b.ms));

    const segments = [];
    byPlate.forEach((arr) => {
      for (let i = 1; i < arr.length; i++) {
        const prev = arr[i - 1], curr = arr[i];
        const km =
          typeof curr.odometerKm === "number" && typeof prev.odometerKm === "number" &&
          curr.odometerKm > prev.odometerKm ? curr.odometerKm - prev.odometerKm : 0;
        const pInfo = plateMap.get(curr.plKey) || {};
        const tesis = curr.tesis || pInfo.tesis || "GENEL";
        segments.push({
          date: curr.date,
          plKey: curr.plKey,
          plDisplay: curr.plDisplay,
          tesis,
          aracCinsi: pInfo.aracCinsi || "-",
          km,
          litre: Number(curr.liters) || 0,
        });
      }
    });

    const periodSeg = segments.filter((s) => inRange(s.date, sDate, eDate));
    const agg = new Map();
    periodSeg.forEach((s) => {
      const key = `${s.tesis}|${s.plKey}`;
      const cur = agg.get(key) || {
        tesis: s.tesis,
        aracCinsi: s.aracCinsi,
        // Ekranda gösterilecek plaka: VERİTABANINDAKİ HALİ
        plaka: s.plDisplay,
        km: 0,
        litre: 0,
      };
      cur.km += s.km;
      cur.litre += s.litre;
      // İstersen son görülen değeri baz almak için şunu da tutabiliriz:
      // cur.plaka = s.plDisplay;
      agg.set(key, cur);
    });

    const rows = [];
    agg.forEach((v) => {
      rows.push({
        tesis: v.tesis,
        aracCinsi: v.aracCinsi,
        plaka: v.plaka,
        km: v.km || null,
        litre: v.litre,
        l100: v.km > 0 ? (v.litre / v.km) * 100 : null,
      });
    });
    rows.sort((a, b) => {
      const te = a.tesis.localeCompare(b.tesis, "tr"); if (te !== 0) return te;
      return a.plaka.localeCompare(b.plaka, "tr");
    });
    return rows;
  }, [startStr, endStr, transactions, plateMap]);

  const driverRows = useMemo(() => {
    const sDate = new Date(`${startStr}T00:00:00`);
    const eDate = new Date(`${endStr}T23:59:59`);
    const allTx = transactions
      .filter((t) => t.status === "approved" && t.sourceType === "tank" && t.deleted !== true)
      .map((t) => ({ ...t, pl: safeUpper(t.vehiclePlate) || "PLAKA-YOK", ms: tsToMs(t.date) }));
    const byPlate = new Map();
    allTx.forEach((t) => { if (!byPlate.has(t.pl)) byPlate.set(t.pl, []); byPlate.get(t.pl).push(t); });
    byPlate.forEach((arr) => arr.sort((a, b) => a.ms - b.ms));

    const segments = [];
    byPlate.forEach((arr) => {
      for (let i = 1; i < arr.length; i++) {
        const prev = arr[i - 1], curr = arr[i];
        const km =
          typeof curr.odometerKm === "number" && typeof prev.odometerKm === "number" &&
          curr.odometerKm > prev.odometerKm ? curr.odometerKm - prev.odometerKm : 0;
        const tesis = curr.tesis || "GENEL";
        segments.push({
          date: curr.date, tesis, km, litre: Number(curr.liters) || 0,
          driverId: curr.driverId || null, driverName: curr.driverName || "ŞOFÖR-YOK",
        });
      }
    });

    const periodSeg = segments.filter((s) => inRange(s.date, sDate, eDate));
    const agg = new Map();
    periodSeg.forEach((s) => {
      const key = `${s.tesis}|${s.driverId ? s.driverId : "name:" + s.driverName}`;
      const cur = agg.get(key) || { tesis: s.tesis, sofor: s.driverName, gorev: "-", km: 0, litre: 0 };
      cur.km += s.km; cur.litre += s.litre;
      if (s.driverId) {
        const person = peopleMap.get(s.driverId);
        if (person) {
          const ad = (person.ad || "").trim(); const soyad = (person.soyad || "").trim();
          cur.sofor = `${ad} ${soyad}`.trim() || cur.sofor;
          cur.gorev = person.gorev || cur.gorev;
        }
      }
      agg.set(key, cur);
    });

    const rows = [];
    agg.forEach((v) => {
      rows.push({ tesis: v.tesis, gorev: v.gorev, sofor: v.sofor, km: v.km || null, litre: v.litre, l100: v.km > 0 ? (v.litre / v.km) * 100 : null });
    });
    rows.sort((a, b) => {
      const te = a.tesis.localeCompare(b.tesis, "tr"); if (te !== 0) return te;
      return (a.sofor || "-").localeCompare(b.sofor || "-", "tr");
    });
    return rows;
  }, [startStr, endStr, transactions, peopleMap]);

  /* ================== TASARIM: FaturaListesi’ne UYUM ================== */
  // Sadece Plaka ve Şoför tabloları için filtre yapısı (FaturaListesi stilinde)
  const plateCols = [
    { key: "tesis", title: "Tesis", numeric: false },
    { key: "aracCinsi", title: "Araç Cinsi", numeric: false },
    { key: "plaka", title: "Plaka", numeric: false },
    { key: "km", title: "KM", numeric: true },
    { key: "litre", title: "Litre", numeric: true },
    { key: "l100", title: "L/100km", numeric: true },
  ];
  const driverCols = [
    { key: "tesis", title: "Tesis", numeric: false },
    { key: "gorev", title: "Görev", numeric: false },
    { key: "sofor", title: "Şoför", numeric: false },
    { key: "km", title: "KM", numeric: true },
    { key: "litre", title: "Litre", numeric: true },
    { key: "l100", title: "L/100km", numeric: true },
  ];

  // Plate filters
  const [plateFilters, setPlateFilters] = useState({});
  const [plateDropdowns, setPlateDropdowns] = useState({});
  const plateDropdownRefs = useRef({});
  const [plateColWidths, setPlateColWidths] = useState({});

  // Driver filters
  const [driverFilters, setDriverFilters] = useState({});
  const [driverDropdowns, setDriverDropdowns] = useState({});
  const driverDropdownRefs = useRef({});
  const [driverColWidths, setDriverColWidths] = useState({});

  // Dış tıklamada popup kapat (FaturaListesi mantığı)
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.entries(plateDropdownRefs.current).forEach(([col, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setPlateDropdowns((prev) => ({ ...prev, [col]: false }));
        }
      });
      Object.entries(driverDropdownRefs.current).forEach(([col, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setDriverDropdowns((prev) => ({ ...prev, [col]: false }));
        }
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // DropDown konumlandırma
  const togglePlateDropdown = (col, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPlateDropdowns((prev) => ({
      ...prev,
      [col]: prev[col] ? false : { top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 160) },
    }));
  };
  const toggleDriverDropdown = (col, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDriverDropdowns((prev) => ({
      ...prev,
      [col]: prev[col] ? false : { top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 160) },
    }));
  };

  // Filtre seçeneklerini üret (diğer filtreler uygulanmış halde)
  const getFilteredOptions = (rows, filters, col, omitCol) => {
    const other = Object.fromEntries(Object.entries(filters).filter(([k]) => k !== omitCol));
    const passes = rows.filter((r) =>
      Object.entries(other).every(([k, vals]) => !vals?.length || vals.includes(String(r[k] ?? "")))
    );
    return [...new Set(passes.map((r) => String(r[col] ?? "")))].sort((a, b) => a.localeCompare(b, "tr"));
  };

  const handleFilterChange = (setFiltersFn, filters, col, val) => {
    setFiltersFn((prev) => {
      const cur = prev[col] || [];
      return cur.includes(val)
        ? { ...prev, [col]: cur.filter((v) => v !== val) }
        : { ...prev, [col]: [...cur, val] };
    });
  };

  const formatSayi = (val) => {
    if (val == null || isNaN(val)) return "";
    return Number(val).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Filtre uygulanmış satırlar ve toplamlar (PLAKA)
  const plateRowsFiltered = useMemo(() => {
    return plateRows.filter((r) =>
      Object.entries(plateFilters).every(([col, vals]) => !vals?.length || vals.includes(String(r[col] ?? "")))
    );
  }, [plateRows, plateFilters]);
  const plateTotals = useMemo(() => {
    return {
      km: plateRowsFiltered.reduce((a, r) => a + (Number(r.km) || 0), 0),
      litre: plateRowsFiltered.reduce((a, r) => a + (Number(r.litre) || 0), 0),
      l100: null, // toplam ortalama yazmıyoruz; istersen ağırlıklı ort. ekleriz
    };
  }, [plateRowsFiltered]);

  // Filtre uygulanmış satırlar ve toplamlar (ŞOFÖR)
  const driverRowsFiltered = useMemo(() => {
    return driverRows.filter((r) =>
      Object.entries(driverFilters).every(([col, vals]) => !vals?.length || vals.includes(String(r[col] ?? "")))
    );
  }, [driverRows, driverFilters]);
  const driverTotals = useMemo(() => {
    return {
      km: driverRowsFiltered.reduce((a, r) => a + (Number(r.km) || 0), 0),
      litre: driverRowsFiltered.reduce((a, r) => a + (Number(r.litre) || 0), 0),
      l100: null,
    };
  }, [driverRowsFiltered]);

  /* ================== UI ================== */
  return (
    <div style={{ padding: 10, position: "relative" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>📊 Raporlar</h2>

      {/* Tarih alanları (AYNI HESAP, sadece minimal stil) */}
      <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: "#334155" }}>Başlangıç</label><br />
          <input
            type="date"
            value={startStr}
            onChange={(e) => setRange((r) => ({ ...r, startStr: e.target.value }))}
            style={{ border: "1px solid #cbd5e1", padding: "6px 8px", borderRadius: 6, height: 28 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#334155" }}>Bitiş</label><br />
          <input
            type="date"
            value={endStr}
            onChange={(e) => setRange((r) => ({ ...r, endStr: e.target.value }))}
            style={{ border: "1px solid #cbd5e1", padding: "6px 8px", borderRadius: 6, height: 28 }}
          />
        </div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {loadingTemp ? "Ortam sıcaklığı güncelleniyor..." : ""}
        </div>
      </div>

      {/* 🛢️ Stok Durumu (Tüm Zaman) — HESAPLAMA AYNEN KALDI */}
      <h3 style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", margin: "12px 0 8px" }}>
        Stok Durumu (Tüm Zaman)
      </h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "11px", tableLayout: "fixed", width: "100%" }}>
          <thead style={{ background: "#f1f5f9" }}>
            <tr>
              {["Tesis", "Mevcut (L)", "Kesafet Sonrası Stok (15°C) (L)", "Düzeltme Toplamı (L)", "Ortam Sıcaklığı (°C)"].map((h) => (
                <th key={h} style={{ border: "1px solid #ccc", padding: 5, textAlign: "left", minWidth: 120 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from(stockByTesisAll.out.keys()).sort((a, b) => a.localeCompare(b, "tr")).map((tesis) => {
              const val = stockByTesisAll.out.get(tesis) || { raw: 0, corrected: 0 };
              const adj15 = stockByTesisAll.adjByTesis[tesis] || 0;
              const temp = stockByTesisAll.tempByTesis[tesis];
              return (
                <tr key={tesis}>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>{tesis}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5, textAlign: "right" }}>{fmt(val.raw)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5, textAlign: "right" }}>{fmt(val.corrected)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5, textAlign: "right" }}>{fmt(adj15)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5, textAlign: "right" }}>
                    {Number.isFinite(temp) ? String(temp.toFixed(1)).replace(".", ",") : "-"}
                  </td>
                </tr>
              );
            })}
            {stockByTesisAll.out.size === 0 && (
              <tr>
                <td colSpan={5} style={{ border: "1px solid #ccc", padding: 8, textAlign: "center", color: "#64748b" }}>Veri yok</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================== PLAKA TABLOSU (FaturaListesi tasarım + filtre) ================== */}
      <h3 style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", margin: "16px 0 8px" }}>
        Dönem Tüketim (Plaka) & KM & L/100km
      </h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "11px", tableLayout: "fixed", width: "100%" }}>
          <thead style={{ background: "#f1f5f9" }}>
            <tr>
              {plateCols.map((c) => (
                <th key={c.key}
                    style={{ border: "1px solid #ccc", padding: 5, textAlign: "left", position: "relative", minWidth: plateColWidths[c.key] || 120 }}>
                  {c.title}
                  {!c.numeric && (
                    <button
                      onClick={(e) => togglePlateDropdown(c.key, e)}
                      style={{
                        marginLeft: 5, fontSize: "10px", border: "1px solid #ccc",
                        borderRadius: 4, backgroundColor: plateFilters[c.key]?.length ? "#bfdbfe" : "white",
                        cursor: "pointer"
                      }}
                    >▼</button>
                  )}
                  {/* Kolon genişliği sürgüsü */}
                  <div
                    onMouseDown={(e) => {
                      const startX = e.clientX; const startWidth = plateColWidths[c.key] || 120;
                      const onMove = (ev) => {
                        const nw = startWidth + (ev.clientX - startX);
                        setPlateColWidths((prev) => ({ ...prev, [c.key]: Math.max(80, nw) }));
                      };
                      const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                      window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
                    }}
                    style={{ cursor: "col-resize", position: "absolute", top: 0, right: 0, width: 5, height: "100%" }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Genel toplam satırı (FaturaListesi tonu) */}
            <tr style={{ background: "#fef9c3", fontWeight: "bold" }}>
              {plateCols.map((c) => (
                <td key={c.key} style={{ border: "1px solid #ccc", padding: 5, textAlign: c.numeric ? "right" : "left" }}>
                  {c.key === "km" ? formatSayi(plateTotals.km)
                    : c.key === "litre" ? formatSayi(plateTotals.litre)
                    : c.key === "l100" ? ("" /* toplama yazmıyoruz */)
                    : ""}
                </td>
              ))}
            </tr>

            {/* Satırlar */}
            {plateRowsFiltered.map((r, i) => (
              <tr key={i}>
                {plateCols.map((c) => (
                  <td key={c.key} style={{ border: "1px solid #ccc", padding: 5, textAlign: c.numeric ? "right" : "left" }}>
                    {c.numeric
                      ? (r[c.key] == null ? "" : formatSayi(r[c.key]))
                      : (r[c.key] || "")}
                  </td>
                ))}
              </tr>
            ))}
            {plateRowsFiltered.length === 0 && (
              <tr><td colSpan={plateCols.length} style={{ border: "1px solid #ccc", padding: 8, textAlign: "center", color: "#64748b" }}>Veri yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Filtre popup'ları (Plaka) */}
      {Object.entries(plateDropdowns).map(([col, pos]) => pos && (
        <div
          key={col}
          ref={(el) => (plateDropdownRefs.current[col] = el)}
          style={{
            position: "fixed", top: pos.top, left: pos.left, background: "white",
            border: "1px solid #ccc", borderRadius: 4, boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            zIndex: 9999, fontSize: "11px", width: pos.width || 180, padding: 5, maxHeight: 260, overflowY: "auto"
          }}
        >
          <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
            <input
              type="checkbox"
              checked={(plateFilters[col]?.length || 0) === getFilteredOptions(plateRows, plateFilters, col, col).length}
              onChange={(e) => {
                const all = getFilteredOptions(plateRows, plateFilters, col, col);
                setPlateFilters((prev) => ({ ...prev, [col]: e.target.checked ? all : [] }));
              }}
            /> Tümünü seç / kaldır
          </label>
          {getFilteredOptions(plateRows, plateFilters, col, col).map((val) => (
            <label key={val} style={{ display: "block", marginBottom: 2 }}>
              <input
                type="checkbox"
                checked={plateFilters[col]?.includes(val) || false}
                onChange={() => handleFilterChange(setPlateFilters, plateFilters, col, val)}
              /> {val || "(boş)"}
            </label>
          ))}
        </div>
      ))}

      {/* ================== ŞOFÖR TABLOSU (FaturaListesi tasarım + filtre) ================== */}
      <h3 style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", margin: "18px 0 8px" }}>
        Dönem Tüketim (Şoför) – KM & Ortalama
      </h3>
      <div style={{ overflowX: "auto", marginBottom: 24 }}>
        <table style={{ borderCollapse: "collapse", fontSize: "11px", tableLayout: "fixed", width: "100%" }}>
          <thead style={{ background: "#f1f5f9" }}>
            <tr>
              {driverCols.map((c) => (
                <th key={c.key}
                    style={{ border: "1px solid #ccc", padding: 5, textAlign: "left", position: "relative", minWidth: driverColWidths[c.key] || 120 }}>
                  {c.title}
                  {!c.numeric && (
                    <button
                      onClick={(e) => toggleDriverDropdown(c.key, e)}
                      style={{
                        marginLeft: 5, fontSize: "10px", border: "1px solid #ccc",
                        borderRadius: 4, backgroundColor: driverFilters[c.key]?.length ? "#bfdbfe" : "white",
                        cursor: "pointer"
                      }}
                    >▼</button>
                  )}
                  <div
                    onMouseDown={(e) => {
                      const startX = e.clientX; const startWidth = driverColWidths[c.key] || 120;
                      const onMove = (ev) => {
                        const nw = startWidth + (ev.clientX - startX);
                        setDriverColWidths((prev) => ({ ...prev, [c.key]: Math.max(80, nw) }));
                      };
                      const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                      window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
                    }}
                    style={{ cursor: "col-resize", position: "absolute", top: 0, right: 0, width: 5, height: "100%" }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Genel toplam satırı */}
            <tr style={{ background: "#fef9c3", fontWeight: "bold" }}>
              {driverCols.map((c) => (
                <td key={c.key} style={{ border: "1px solid #ccc", padding: 5, textAlign: c.numeric ? "right" : "left" }}>
                  {c.key === "km" ? formatSayi(driverTotals.km)
                    : c.key === "litre" ? formatSayi(driverTotals.litre)
                    : c.key === "l100" ? ("" /* toplama yazmıyoruz */)
                    : ""}
                </td>
              ))}
            </tr>

            {/* Satırlar */}
            {driverRowsFiltered.map((r, i) => (
              <tr key={i}>
                {driverCols.map((c) => (
                  <td key={c.key} style={{ border: "1px solid #ccc", padding: 5, textAlign: c.numeric ? "right" : "left" }}>
                    {c.numeric
                      ? (r[c.key] == null ? "" : formatSayi(r[c.key]))
                      : (r[c.key] || "")}
                  </td>
                ))}
              </tr>
            ))}
            {driverRowsFiltered.length === 0 && (
              <tr><td colSpan={driverCols.length} style={{ border: "1px solid #ccc", padding: 8, textAlign: "center", color: "#64748b" }}>Veri yok</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Filtre popup'ları (Şoför) */}
      {Object.entries(driverDropdowns).map(([col, pos]) => pos && (
        <div
          key={col}
          ref={(el) => (driverDropdownRefs.current[col] = el)}
          style={{
            position: "fixed", top: pos.top, left: pos.left, background: "white",
            border: "1px solid #ccc", borderRadius: 4, boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            zIndex: 9999, fontSize: "11px", width: pos.width || 180, padding: 5, maxHeight: 260, overflowY: "auto"
          }}
        >
          <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
            <input
              type="checkbox"
              checked={(driverFilters[col]?.length || 0) === getFilteredOptions(driverRows, driverFilters, col, col).length}
              onChange={(e) => {
                const all = getFilteredOptions(driverRows, driverFilters, col, col);
                setDriverFilters((prev) => ({ ...prev, [col]: e.target.checked ? all : [] }));
              }}
            /> Tümünü seç / kaldır
          </label>
          {getFilteredOptions(driverRows, driverFilters, col, col).map((val) => (
            <label key={val} style={{ display: "block", marginBottom: 2 }}>
              <input
                type="checkbox"
                checked={driverFilters[col]?.includes(val) || false}
                onChange={() => handleFilterChange(setDriverFilters, driverFilters, col, val)}
              /> {val || "(boş)"}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}
